import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { RecordNotFoundError } from '@/lib/errors'
import { supabase } from '@/lib/supabase/client'

export type Workspace = Database['public']['Tables']['workspaces']['Row']
type WorkspaceInsert = Database['public']['Tables']['workspaces']['Insert']
type WorkspaceUpdate = Database['public']['Tables']['workspaces']['Update']

async function createWorkspace(
  input: Pick<WorkspaceInsert, 'org_id' | 'name' | 'icon'> & {
    description?: string | null
    visibility?: 'public' | 'private'
  }
) {
  const { data, error } = await supabase
    .from('workspaces')
    .insert({
      org_id: input.org_id,
      name: input.name,
      description: input.description ?? null,
      icon: input.icon ?? 'briefcase',
      visibility: input.visibility ?? 'public',
    })
    .select('*')
    .single()

  if (error) {
    throw new Error(`Failed to create workspace: ${error.message}`, {
      cause: error,
    })
  }

  if (!data) {
    throw new Error('Failed to create workspace: no data returned')
  }

  return data
}

async function updateWorkspace(
  workspaceId: string,
  input: Pick<
    WorkspaceUpdate,
    'name' | 'description' | 'icon' | 'generation_settings' | 'visibility'
  >
) {
  if (!workspaceId || workspaceId.trim() === '') {
    throw new Error(
      'Invalid workspaceId: cannot update workspace without valid workspace ID'
    )
  }

  const { data, error } = await supabase
    .from('workspaces')
    .update({
      name: input.name,
      description: input.description,
      icon: input.icon,
      generation_settings: input.generation_settings,
      visibility: input.visibility,
    })
    .eq('id', workspaceId)
    .select('*')
    .single()

  if (error) {
    throw new Error(`Failed to update workspace: ${error.message}`, {
      cause: error,
    })
  }

  if (!data) {
    throw new Error('Workspace not found')
  }

  return data
}

async function archiveWorkspace(workspaceId: string) {
  if (!workspaceId || workspaceId.trim() === '') {
    throw new Error(
      'Invalid workspaceId: cannot archive workspace without valid workspace ID'
    )
  }

  const { error } = await supabase
    .from('workspaces')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', workspaceId)

  if (error) {
    throw new Error(`Failed to archive workspace: ${error.message}`, {
      cause: error,
    })
  }
}

export const workspace = {
  findOne: (workspaceId: string) =>
    queryOptions({
      queryKey: ['workspace', workspaceId],
      queryFn: async () => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch workspace without valid workspace ID'
          )
        }

        const { data, error } = await supabase
          .from('workspaces')
          .select('*')
          .eq('id', workspaceId)
          .is('archived_at', null)
          .maybeSingle()

        if (error) {
          throw new Error(`Failed to fetch workspace: ${error.message}`, {
            cause: error,
          })
        }

        if (!data) {
          throw new RecordNotFoundError('Workspace not found')
        }

        return data
      },
    }),

  findMany: (orgId: string) =>
    queryOptions({
      queryKey: ['workspaces', 'org', orgId],
      queryFn: async () => {
        if (!orgId || orgId.trim() === '') {
          throw new Error(
            'Invalid orgId: cannot fetch workspaces without valid organization ID'
          )
        }

        const { data, error } = await supabase
          .from('workspaces')
          .select('*')
          .eq('org_id', orgId)
          .is('archived_at', null)
          .order('updated_at', { ascending: false })

        if (error) {
          throw new Error(`Failed to fetch workspaces: ${error.message}`, {
            cause: error,
          })
        }

        return data
      },
    }),

  create: createWorkspace,
  update: updateWorkspace,
  archive: archiveWorkspace,
}
